import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as EmailValidator from 'email-validator';
import { shallow } from 'zustand/shallow';

import { PhoneParams } from '../../api/Rest/users';
import {
  ResetPasswordModal,
  ResetPasswordResendModal,
  VerifyEmailModal,
  VerifyEmailResendModal,
} from '../../components/Molecules';
import { PhoneCodePopup } from '../../components/Popups/PhoneCodePopup';
import { ForgotPasswordTemplate } from '../../components/Templates';
import { PhoneVerificationTemplate } from '../../components/Templates';
import { RecentLoginInfo } from '../../components/Templates/Auth/ForgotPasswordTemplate/ForgotPasswordTemplate';
import { RegionsCode } from '../../constants/region';
import { AppToastErrorOption } from '../../constants/toast';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import {
  Email,
  Password,
  Phone,
  PhoneVerificationJson,
  Profile,
  ResetPasswordResult,
  UserListType,
} from '../../models/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { deepTransformDate } from '../../utils/date';
import { getRegionFromPhoneCode } from '../../utils/region';

const {
  validatePhone,
  findProfile,
  forgotPassword,
  forgotPasswordWithPhone,
  sendVerification,
  getCurrentUser,
} = useUserStore.getState();

enum ForgotPasswordModalType {
  ResetPassword = 'resetPassword',
  ResetPasswordResend = 'resetPasswordResend',
  VerifyEmail = 'verifyEmail',
  VerifyEmailResend = 'verifyEmailResend',
}

interface HiddenParams {
  username?: string;
  email?: string;
  phone?: PhoneParams;
}
interface State {
  activeTab: 'email' | 'phone';
  username: string;
  hiddenParams: HiddenParams;
  phoneCode: string;
  phoneNumber: string;
  profile: Profile | undefined | null;
  email: Email | undefined;
  phone: Phone | undefined;
  password: Password | undefined;
  isLoading: boolean;
  showPopup: ForgotPasswordModalType | undefined;
  errorMessage: string | undefined;
  phoneVerification: PhoneVerificationJson | undefined;
}

interface NavigationState {
  activeTab?: 'email' | 'phone';
  email?: string;
  phoneCode?: string;
  phoneNumber?: string;
}

interface ForgotPasswordScreenProps {
  webPageType: WebPageType;
}

const ForgotPasswordScreen: React.FC<ForgotPasswordScreenProps> = ({
  webPageType,
}) => {
  const { state: navigationState } = useLocation() as {
    state: NavigationState | null;
  };
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    activeTab: navigationState?.activeTab ?? 'email',
    username: navigationState?.email ?? '',
    hiddenParams: { email: navigationState?.email ?? '' },
    phoneCode: navigationState?.phoneCode ?? '+852',
    phoneNumber: navigationState?.phoneNumber ?? '',
    email: undefined,
    phone: undefined,
    password: undefined,
    profile: undefined,
    isLoading: false,
    showPopup: undefined,
    errorMessage: undefined,
    phoneVerification: undefined,
  });

  const { setSharedData } = useShareData();

  const activeUserId = useSessionStore((store) => store.activeUserId);

  const userAccount = useUserStore(
    (store) => store.getAccount(activeUserId ?? ''),
    shallow,
  );
  const userProfile = useUserStore(
    (store) => store.getProfile(activeUserId ?? ''),
    shallow,
  );

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  useEffect(() => {
    if (activeUserId) {
      getCurrentUser().catch(console.error);
    }
  }, [activeUserId]);

  useEffect(() => {
    if (userAccount && userProfile) {
      setState((s) => ({
        ...s,
        profile: userProfile,
        email: deepTransformDate(userAccount.email) as unknown as Email,
        phone: userAccount.phone ?? undefined, // may need to mask number
        password: deepTransformDate(
          userAccount.password,
        ) as unknown as Password,
        hiddenParams: { username: userAccount.username },
      }));
    }
  }, [userAccount, userProfile]);

  const onSelectTab = useCallback((tab: 'email' | 'phone') => {
    setState((s) => ({ ...s, activeTab: tab, errorMessage: '' }));
  }, []);

  const onUsernameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        username: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const onPhoneNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        phoneNumber: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const { showPopup, hidePopup } = usePopup();
  const onClickPhoneCode = useCallback(() => {
    showPopup(
      <PhoneCodePopup
        phoneCode={state.phoneCode}
        onPrimary={hidePopup}
        onCode={(selectedCode) => {
          setState((prevState) => ({ ...prevState, phoneCode: selectedCode }));
          hidePopup();
        }}
        webPageType={webPageType}
      />,
    );
  }, [showPopup, state.phoneCode, hidePopup, webPageType]);

  function maskEmail(email: string): string {
    const parts: string[] = email.split('@');
    if (parts.length === 2) {
      const firstPart: string = parts[0]!;
      const secondPart: string = parts[1]!;
      if (firstPart.length > 3 && secondPart.length > 3) {
        const prefix: string = firstPart.substring(0, 3);
        const suffix: string = secondPart.substring(3);
        return `${prefix}***@***${suffix}`;
      } else if (firstPart.length > 3) {
        const prefix: string = firstPart.substring(0, 3);
        return `${prefix}***@${secondPart}`;
      } else if (secondPart.length > 3) {
        const suffix: string = secondPart.substring(3);
        return `${firstPart}@***${suffix}`;
      }
    }
    return email;
  }
  function maskPhoneNumber(phoneNumber: string): string {
    return phoneNumber.replace(
      /(\+\d{1,3}\s\d{0,2})([0-9\s]+)(\d{2})/,
      (match, start, middle: string, end) =>
        `${start}${middle.replace(/\d/g, '*')}${end}`,
    );
  }

  const onClickSearch = useCallback(async () => {
    if (state.activeTab === 'email' && !state.username) {
      setState((s) => ({
        ...s,
        errorMessage: 'Enter your email / username',
      }));
    } else if (state.activeTab === 'phone' && !state.phoneNumber) {
      setState((s) => ({
        ...s,
        errorMessage: 'Enter your phone number',
      }));
    } else {
      setState((s) => ({ ...s, isLoading: true, errorMessage: undefined }));
      const isEmail = EmailValidator.validate(state.username);
      const params = {
        email:
          state.activeTab === 'email' && isEmail ? state.username : undefined,
        username:
          state.activeTab === 'email' && !isEmail ? state.username : undefined,
        phone:
          state.activeTab === 'phone'
            ? {
                region:
                  getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
                phoneNumber: state.phoneNumber,
              }
            : undefined,
      };
      const res = await findProfile(params, {
        headers: { 'cache-control': 'no-cache' },
      });

      setState((s) => ({ ...s, isLoading: false }));
      if (res.ok) {
        if (res.data.profile) {
          const profile = new Profile(res.data.profile);
          setState((s) => ({
            ...s,
            profile: profile,
            email: res.data.email,
            phone: res.data.phone,
            password: res.data.password,
            hiddenParams: params,
          }));
          return;
        }
      }
      setState((s) => ({
        ...s,
        errorMessage:
          state.activeTab === 'email'
            ? 'No accounts matched. Please make sure you have entered the correct email address or username.'
            : 'No accounts matched. Please make sure you have entered the correct phone number.',
      }));
    }
  }, [state.activeTab, state.phoneCode, state.phoneNumber, state.username]);

  const onClickReturn = useCallback(() => {
    setState((s) => ({
      ...s,
      profile: undefined,
      email: undefined,
      password: undefined,
    }));
  }, []);

  const onCloseModal = useCallback(() => {
    setState((s) => ({ ...s, showPopup: undefined }));
  }, []);

  const onClickSendVerification = useCallback(async () => {
    if (state.profile?.userId) {
      setState((s) => ({ ...s, isLoading: true, errorMessage: undefined }));
      const res = await sendVerification({
        userId: state.profile?.userId,
      });
      const findProfileRes = await findProfile(state.hiddenParams, {
        headers: { 'cache-control': 'no-cache' },
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          showPopup: ForgotPasswordModalType.VerifyEmailResend,
        }));
        if (findProfileRes.ok) {
          setState((s) => ({
            ...s,
            email: findProfileRes.data.email,
            password: findProfileRes.data.password,
          }));
        }
      } else {
        toast(t('The sending interval is too short. Please try again later.'), {
          ...AppToastErrorOption,
          id: 'short-Interval',
        });
      }
    } else {
      toast(t('There is something wrong.'), {
        ...AppToastErrorOption,
        id: 'error',
      });
    }
  }, [state.profile?.userId, state.hiddenParams, t]);

  const onClickSendForgotPassword = useCallback(async () => {
    if (state.profile?.userId) {
      setState((s) => ({ ...s, isLoading: true, errorMessage: undefined }));
      const passwordData = await forgotPassword({
        userId: state.profile?.userId,
      });
      const findProfileRes = await findProfile(state.hiddenParams, {
        headers: { 'cache-control': 'no-cache' },
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (passwordData) {
        setState((s) => ({
          ...s,
          showPopup: ForgotPasswordModalType.ResetPassword,
        }));
        if (findProfileRes.ok) {
          setState((s) => ({
            ...s,
            email: findProfileRes.data.email,
            password: passwordData,
          }));
        }
      } else {
        toast(t('The sending interval is too short. Please try again later.'), {
          ...AppToastErrorOption,
          id: 'short-Interval',
        });
      }
    } else {
      toast(t('There is something wrong.'), {
        ...AppToastErrorOption,
        id: 'error',
      });
    }
  }, [state.profile?.userId, state.hiddenParams, t]);

  const onClickSendEmail = useCallback(() => {
    if (state.email?.address) {
      if (state.email?.isVerified) {
        if (
          state.password?.resetStartedAt &&
          Date.now() < state.password?.resetStartedAt + 60 * 60 * 1000
        ) {
          setState((s) => ({
            ...s,
            showPopup: ForgotPasswordModalType.ResetPasswordResend,
          }));
        } else {
          onClickSendForgotPassword().catch(console.error);
        }
      } else {
        if (Date.now() < state.email?.verificationSentAt + 60 * 60 * 1000) {
          setState((s) => ({
            ...s,
            showPopup: ForgotPasswordModalType.VerifyEmailResend,
          }));
        } else {
          setState((s) => ({
            ...s,
            showPopup: ForgotPasswordModalType.VerifyEmail,
          }));
        }
      }
    } else {
      toast(t('This account has no email.'), {
        ...AppToastErrorOption,
        id: 'no-email',
      });
    }
  }, [
    onClickSendForgotPassword,
    state.email?.address,
    state.email?.isVerified,
    state.email?.verificationSentAt,
    state.password?.resetStartedAt,
    t,
  ]);

  const onClickSendSms = useCallback(async () => {
    if (state.phone && state.profile?.userId) {
      setState((s) => ({ ...s, isLoading: true, errorMessage: undefined }));
      const res = await forgotPasswordWithPhone({
        userId: state.profile.userId,
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (res) {
        setState((s) => ({ ...s, phoneVerification: res }));
      } else {
        toast(t('There is something wrong.'), AppToastErrorOption);
      }
    } else {
      toast(t('This account has no phone.'), AppToastErrorOption);
    }
  }, [state.phone, state.profile?.userId, t]);

  const onClickVerifyCode = useCallback(
    async (code: string) => {
      if (state.phoneVerification) {
        setState((s) => ({
          ...s,
          isClicked: true,
          isLoading: true,
          errorMessage: '',
        }));

        const phone = {
          region: state.phoneVerification.region,
          phoneNumber: state.phoneVerification.phoneNumber,
          verificationCode: code,
        };
        const result = await validatePhone(phone);
        if (result === ResetPasswordResult.Success) {
          setState((s) => ({ ...s, isLoading: false }));
          navigate(getWebPageLink(webPageType, WebPageLink.ResetPassword), {
            state: { phone },
          });
        } else {
          setState((s) => ({
            ...s,
            isLoading: false,
            errorMessage: 'Incorrect Code',
          }));
        }
      }
    },
    [navigate, state.phoneVerification, webPageType],
  );

  const onClickRecentAcc = useCallback(
    async ({ email, phone }: { email: Email | null; phone: Phone | null }) => {
      if (email || phone) {
        // await onClickSearch();
        setState((s) => ({ ...s, isLoading: true, errorMessage: undefined }));
        const params = { email: email?.address, phone: phone ?? undefined };
        const res = await findProfile(params, {
          headers: { 'cache-control': 'no-cache' },
        });
        setState((s) => ({ ...s, isLoading: false }));
        if (res.ok) {
          if (res.data.profile) {
            const profile = new Profile(res.data.profile);
            setState((s) => ({
              ...s,
              profile: profile,
              email: res.data.email,
              phone: res.data.phone,
              password: res.data.password,
              hiddenUsername: params,
            }));
          } else {
            setState((s) => ({
              ...s,
              errorMessage:
                'No accounts matched. Please make sure you have entered the correct email address or username.',
            }));
          }
        }
      } else {
        toast(t('This account has no email and phone.'), {
          ...AppToastErrorOption,
          id: 'no-email',
        });
      }
    },
    [t],
  );

  const recentLogins = useMemo<RecentLoginInfo[]>(() => {
    const store = useUserStore.getState();
    const recentAccounts = store.accounts.list(UserListType.RecentLogin).get();
    const recentProfiles = store.profiles.list(UserListType.RecentLogin).get();

    return recentAccounts
      .map((account, index) => {
        const profile = recentProfiles[index];
        if (!profile || !account) return null;
        return {
          icon: profile.avatar || undefined,
          userId: profile.userId || undefined,
          source: profile.profilePicture || undefined,
          name: profile.name || undefined,
          email: account.email?.address
            ? maskEmail(account.email.address)
            : undefined,
          phoneNumber: account.phone?.phoneNumber
            ? maskPhoneNumber(account.phone?.phoneNumber)
            : undefined,
          onClick: () => {
            onClickRecentAcc({
              email: deepTransformDate(account.email) as unknown as Email,
              phone: account.phone,
            }).catch(console.error);
          },
        } as RecentLoginInfo;
      })
      .filter((item): item is RecentLoginInfo => item !== null);
  }, [onClickRecentAcc]);

  if (state.phoneVerification) {
    const { phoneNumber, verificationSentAt } = state.phoneVerification;

    return (
      <PhoneVerificationTemplate
        phoneNumber={maskPhoneNumber(phoneNumber)}
        resendCountDown={verificationSentAt + 60 * 1000}
        errorMessage={state.errorMessage ?? ''}
        isLoading={state.isLoading}
        webPageType={webPageType}
        onClickConfirm={(code) => {
          onClickVerifyCode(code).catch(console.error);
        }}
        onClickResend={() => {
          onClickSendSms().catch(console.error);
        }}
        onClickBack={() => {
          setState((s) => ({ ...s, phoneVerification: undefined }));
        }}
      />
    );
  }

  return (
    <>
      <ForgotPasswordTemplate
        webPageType={webPageType}
        activeTab={state.activeTab}
        onSelectTab={onSelectTab}
        username={{ value: state.username, onChange: onUsernameChange }}
        phoneCode={state.phoneCode}
        phoneNumber={{
          value: state.phoneNumber,
          onChange: onPhoneNumberChange,
        }}
        profile={state.profile}
        email={maskEmail(state.email?.address ?? '')}
        phone={maskPhoneNumber(state.phone?.phoneNumber ?? '')}
        onClickSearch={() => {
          onClickSearch().catch(console.error);
        }}
        onClickReturn={onClickReturn}
        onClickSendEmail={onClickSendEmail}
        onClickSendSms={() => {
          onClickSendSms().catch(console.error);
        }}
        isLoading={state.isLoading}
        errorMessage={state.errorMessage}
        recentLoginInfo={recentLogins}
        onClickPhoneCode={onClickPhoneCode}
      />
      {state.showPopup === ForgotPasswordModalType.ResetPassword && (
        <ResetPasswordModal
          profile={state.profile}
          webPageType={webPageType}
          email={maskEmail(state.email?.address ?? '')}
          onClose={onCloseModal}
        />
      )}
      {state.showPopup === ForgotPasswordModalType.ResetPasswordResend && (
        <ResetPasswordResendModal
          email={maskEmail(state.email?.address ?? '')}
          countDown={
            state.password?.resetStartedAt
              ? state.password?.resetStartedAt + 60 * 60 * 1000
              : 0
          }
          webPageType={webPageType}
          onClose={onCloseModal}
          onYes={() => {
            onClickSendVerification().catch(console.error);
          }}
        />
      )}
      {state.showPopup === ForgotPasswordModalType.VerifyEmail && (
        <VerifyEmailModal
          email={maskEmail(state.email?.address ?? '')}
          webPageType={webPageType}
          onYes={() => {
            onClickSendVerification().catch(console.error);
          }}
          onClose={onCloseModal}
        />
      )}
      {state.showPopup === ForgotPasswordModalType.VerifyEmailResend && (
        <VerifyEmailResendModal
          email={maskEmail(state.email?.address ?? '')}
          countDown={
            state.email?.verificationSentAt
              ? state.email?.verificationSentAt + 60 * 60 * 1000
              : 0
          }
          webPageType={webPageType}
          onClose={onCloseModal}
          onYes={() => {
            onClickSendVerification().catch(console.error);
          }}
        />
      )}
    </>
  );
};

export default ForgotPasswordScreen;
